import { useVendorAccess } from '@/hooks/useVendorAccess';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getVendorProducts } from '@/data/vendorDashboard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

export default function VendorDashboard() {
  useVendorAccess();
  const { user } = useAuth();
  
  const { data: products, isLoading } = useQuery({
    queryKey: ['vendor-products', user?.email],
    queryFn: () => getVendorProducts(user?.email ?? ''),
    enabled: !!user?.email,
  });
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Vendor Dashboard</h1>
          <p className="text-muted-foreground">
            Manage your Circle listings and submit updates for review.
          </p>
        </div>
        
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6">
                <Skeleton className="w-full h-40 rounded-lg mb-4" />
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-4" />
                <Skeleton className="h-10 w-full" />
              </Card>
            ))}
          </div>
        ) : products?.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No listings found.</p>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {products?.map((product) => (
              <Card key={product.id} className="p-6 hover:shadow-lg transition-shadow">
                <img 
                  src={product.image || '/placeholder.svg'} 
                  alt={product.title}
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <h3 className="font-bold text-lg mb-2 line-clamp-2">{product.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="outline">{product.status}</Badge>
                  {product.draft_data && (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                      Pending changes
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Link to={`/vendor/edit/${product.id}`} className="flex-1">
                    <Button className="w-full">Edit Listing</Button>
                  </Link>
                  <Link to={`/vendor/change-request/${product.id}`} className="flex-1">
                    <Button variant="outline" className="w-full">Request Changes</Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
